import { Component, computed, effect, inject, input } from '@angular/core';
import { DeadLetterCard } from './dead-letter-card';
import { FilterBar, FilterChip, FilterSelect } from './filter-bar';
import { ServiceBusEntity } from './models';
import {
  SERVICE_BUS_FILTERS,
  ServiceBusStatusKey,
  ServiceBusViewState,
  byAttention,
  statusKey,
  writeServiceBusViewToUrl,
} from './service-bus-state';
import { Tone } from './tone';

const CHIP_TONES: Record<ServiceBusStatusKey, Tone> = {
  DeadLettered: 'bad',
  Clean: 'ok',
  Unreadable: 'warn',
};

// Search, filter and cards of every watched queue and topic. The page loads the data; this owns what is shown of it.
@Component({
  selector: 'app-service-bus',
  imports: [DeadLetterCard, FilterBar],
  templateUrl: './service-bus.html',
  styleUrl: './service-bus.css',
})
export class ServiceBus {
  readonly entities = input.required<ServiceBusEntity[]>();
  readonly now = input(new Date());
  readonly staleAfterMs = input(Infinity);

  private readonly state = inject(ServiceBusViewState);
  protected readonly query = this.state.query;
  protected readonly selected = this.state.selected;
  protected readonly namespace = this.state.namespace;

  protected readonly hasFilter = computed(
    () => this.selected().size > 0 || this.query().trim() !== '' || this.namespace() !== '',
  );

  protected readonly selects = computed<FilterSelect[]>(() => {
    const counts = new Map<string, number>();
    for (const entity of this.entities()) {
      counts.set(entity.namespace, (counts.get(entity.namespace) ?? 0) + 1);
    }
    // A namespace from the address stays selectable even when it has nothing (any more).
    if (this.namespace() !== '' && !counts.has(this.namespace())) {
      counts.set(this.namespace(), 0);
    }
    if (counts.size <= 1) {
      return [];
    }
    const options = [...counts]
      .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map(([name, count]) => ({ value: name, label: `${name} (${count})` }));
    return [
      {
        id: 'namespace',
        label: 'Namespace',
        value: this.namespace(),
        options: [{ value: '', label: 'All' }, ...options],
      },
    ];
  });

  // The namespace and the search narrow the cards first, so the counts on the buttons follow them.
  private readonly searched = computed(() => {
    const namespace = this.namespace();
    const query = this.query().trim().toLowerCase();
    const inNamespace =
      namespace === ''
        ? this.entities()
        : this.entities().filter((entity) => entity.namespace === namespace);
    return query === ''
      ? inNamespace
      : inNamespace.filter((entity) =>
          [entity.name, entity.namespace, ...entity.subscriptions.map((s) => s.name)].some(
            (value) => value.toLowerCase().includes(query),
          ),
        );
  });

  protected readonly matchCount = computed(() => this.searched().length);

  protected readonly chips = computed<FilterChip[]>(() => {
    const searched = this.searched();
    const selected = this.selected();
    return SERVICE_BUS_FILTERS.flatMap(({ status, label, always }) => {
      const count = searched.filter((entity) => statusKey(entity) === status).length;
      const pressed = selected.has(status);
      return always || count > 0 || pressed
        ? [{ key: status, label, count, pressed, tone: CHIP_TONES[status] }]
        : [];
    });
  });

  protected readonly visible = computed(() => {
    const selected = this.selected();
    const matching =
      selected.size === 0
        ? this.searched()
        : this.searched().filter((entity) => selected.has(statusKey(entity)));
    return [...matching].sort(byAttention);
  });

  constructor() {
    effect(() =>
      writeServiceBusViewToUrl({
        query: this.query(),
        selected: this.selected(),
        namespace: this.namespace(),
      }),
    );
  }

  protected toggleFilter(key: string): void {
    const status = key as ServiceBusStatusKey;
    const next = new Set(this.selected());
    if (!next.delete(status)) {
      next.add(status);
    }
    this.selected.set(next);
  }

  protected clearFilter(): void {
    this.selected.set(new Set());
  }

  protected clearAll(): void {
    this.query.set('');
    this.namespace.set('');
    this.selected.set(new Set());
  }

  protected change(change: { id: string; value: string }): void {
    if (change.id === 'namespace') {
      this.namespace.set(change.value);
    }
  }
}
